/**
 * HTTP 请求工具
 * 自动携带 token，处理请求/响应拦截
 */

import Vue from 'vue'
import router from '../router'

// 获取 API 基础地址
const getBaseUrl = () => {
    return Vue.prototype.$wecomConfig?.signatureApi || ''
}

// 获取 token
const getToken = () => {
    return localStorage.getItem('token') || ''
}

// 设置 token
export const setToken = (token) => {
    localStorage.setItem('token', token)
}

// 移除 token
export const removeToken = () => {
    localStorage.removeItem('token')
}

// 检查是否已登录
export const isLoggedIn = () => {
    return !!getToken()
}

// 保存用户信息
export const setUserInfo = (userInfo) => {
    localStorage.setItem('username', JSON.stringify(userInfo))
}

// 获取用户信息
export const getUserInfo = () => {
    const userStr = localStorage.getItem('username')
    if (userStr) {
        try {
            return JSON.parse(userStr)
        } catch (e) {
            return null
        }
    }
    return null
}

// 移除用户信息
export const removeUserInfo = () => {
    localStorage.removeItem('username')
}

// 清除所有登录信息
export const clearAuth = () => {
    removeToken()
    removeUserInfo()
}

/**
 * 统一请求方法
 * @param {string} url - 请求地址
 * @param {object} options - 请求配置
 * @returns {Promise}
 */
const request = async (url, options = {}) => {
    const baseUrl = getBaseUrl()
    const token = getToken()
    
    // 默认配置
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json;charset=UTF-8',
        },
    }
    
    // 合并配置
    const mergedOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    }
    
    // 自动携带 token
    if (token) {
        mergedOptions.headers['token'] = token
        mergedOptions.headers['Authorization'] = 'Bearer ' + token
    }
    
    // 完整 URL
    const fullUrl = url.startsWith('http') ? url : baseUrl + url
    
    try {
        const response = await fetch(fullUrl, mergedOptions)
        const data = await response.json()
        
        // 响应拦截：处理 token 失效
        if (data.code === 201 || data.code === 401) {
            console.warn('Token 失效，跳转登录页')
            removeToken()
            // 跳转登录页
            if (router.currentRoute.path !== '/login') {
                router.replace('/login')
            }
            return Promise.reject(new Error(data.msg || '登录已过期，请重新登录'))
        }
        
        return data
    } catch (error) {
        console.error('请求失败:', error)
        throw error
    }
}

/**
 * GET 请求
 * @param {string} url - 请求地址
 * @param {object} params - 查询参数
 * @param {object} options - 额外配置
 */
export const get = (url, params = {}, options = {}) => {
    // 构建查询字符串
    const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&')
    
    const fullUrl = queryString ? `${url}?${queryString}` : url
    
    return request(fullUrl, {
        method: 'GET',
        ...options,
    })
}

/**
 * POST 请求
 * @param {string} url - 请求地址
 * @param {object} data - 请求体数据
 * @param {object} options - 额外配置
 */
export const post = (url, data = {}, options = {}) => {
    return request(url, {
        method: 'POST',
        body: JSON.stringify(data),
        ...options,
    })
}

/**
 * PUT 请求
 */
export const put = (url, data = {}, options = {}) => {
    return request(url, {
        method: 'PUT',
        body: JSON.stringify(data),
        ...options,
    })
}

/**
 * DELETE 请求
 */
export const del = (url, options = {}) => {
    return request(url, {
        method: 'DELETE',
        ...options,
    })
}

/**
 * 文件上传请求（FormData 方式）
 * @param {string} url - 上传接口地址
 * @param {File} file - 要上传的文件对象
 * @returns {Promise} 包含 code、fileName 等字段的响应
 */
export const upload = async (url, file) => {
    const baseUrl = getBaseUrl()
    const token = getToken()
    const formData = new FormData()
    formData.append('file', file)

    const headers = {}
    if (token) {
        headers['token'] = token
        headers['Authorization'] = 'Bearer ' + token
    }
    // 注意：不设置 Content-Type，让浏览器自动生成 multipart/form-data boundary
    const fullUrl = url.startsWith('http') ? url : baseUrl + url
    try {
        const response = await fetch(fullUrl, {
            method: 'POST',
            headers,
            body: formData,
        })
        return await response.json()
    } catch (error) {
        console.error('上传失败:', error)
        throw error
    }
}

// 默认导出
export default {
    get,
    post,
    put,
    del,
    upload,
    setToken,
    removeToken,
    isLoggedIn,
    setUserInfo,
    getUserInfo,
    removeUserInfo,
    clearAuth,
}
